import apiClient from "@lift-admin/utils/apiClient";

const PLAYWRIGHT_RUNS_PATH = "/event-ingress/events/playwright-tests";
const skipAuthHeaders = { "x-skip-auth-end": "dev" };

export type PlaywrightRunStatus = "queued" | "running" | "succeeded" | "failed";

export interface PlaywrightRunRequest {
  environment: string;
  testName?: string;
  testNames?: string[];
  labels?: string[];
}

export interface PlaywrightCatalogTest {
  title: string;
  tags: string[];
}

export interface PlaywrightCatalogSpec {
  id: string;
  file: string;
  title: string;
  tests: PlaywrightCatalogTest[];
}

export interface PlaywrightTestCatalog {
  specs: PlaywrightCatalogSpec[];
  suggestedLabels: string[];
  ready: boolean;
}

export interface PlaywrightRunSummary {
  total?: number;
  passed?: number;
  failed?: number;
  skipped?: number;
}

// The worker stores summary as counts, while queued runs have none, so the
// table has to render text rather than the raw object.
export const formatRunSummary = (
  summary?: PlaywrightRunSummary | string | null,
): string => {
  if (!summary) return "";
  if (typeof summary === "string") return summary;
  const { total = 0, passed = 0, failed = 0, skipped = 0 } = summary;
  const parts = [`${passed}/${total} passed`];
  if (failed) parts.push(`${failed} failed`);
  if (skipped) parts.push(`${skipped} skipped`);
  return parts.join(", ");
};

export interface PlaywrightRun {
  id: string;
  eventId: string;
  status: PlaywrightRunStatus;
  environment: string;
  source: string;
  testName?: string;
  labels: string[];
  summary?: PlaywrightRunSummary | string | null;
  startedAt?: string;
  finishedAt?: string;
  gcsReportReady: boolean;
}

export interface PlaywrightLogEntry {
  ts: string;
  level: string;
  message: string;
  spec?: string;
  test?: string;
  testKey?: string;
  status?: string;
  labels?: string[];
}

export interface PlaywrightTest {
  id: string;
  testKey?: string;
  spec: string;
  title: string;
  status: string;
  durationMs?: number;
  labels?: string[];
  attempts?: number;
  artifactCount?: number;
}

export type PlaywrightArtifactKind = "screenshot" | "video" | "trace" | "log";

export interface PlaywrightArtifact {
  id: string;
  testKey: string;
  testId: string;
  spec: string;
  test: string;
  attempt: number;
  kind: PlaywrightArtifactKind;
  name: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  labels: string[];
  previewUrl?: string;
  inlineUrl?: string;
}

export interface PlaywrightArtifactBundles {
  run: boolean;
  tests: string[];
}

export interface PlaywrightArtifactManifest {
  runId?: string;
  environment?: string;
  generatedAt?: string | null;
  artifactsReady: boolean;
  artifactCount: number;
  artifactError?: string | null;
  bundleReady?: boolean;
  bundles?: PlaywrightArtifactBundles;
  artifacts: PlaywrightArtifact[];
}

export interface PlaywrightRunReport {
  messageId: string;
  eventId: string;
  status: PlaywrightRunStatus;
  environment: string;
  source: string;
  startedAt?: string;
  finishedAt?: string;
  summary?: PlaywrightRunSummary | string | null;
  labels: string[];
  tests: PlaywrightTest[];
  entries: PlaywrightLogEntry[];
}

export interface PlaywrightRunFilters {
  label?: string[];
  status?: string;
  q?: string;
}

export const requestPlaywrightRun = async (request: PlaywrightRunRequest) => {
  const payload = {
    environment: request.environment,
    ...(request.testName ? { testName: request.testName } : {}),
    ...(request.testNames?.length ? { testNames: request.testNames } : {}),
    ...(request.labels?.length ? { labels: request.labels } : {}),
  };
  const response = await apiClient.post(
    PLAYWRIGHT_RUNS_PATH,
    {
      event_type: "playwright.tests.requested",
      source: "lift-admin",
      payload,
    },
    { headers: skipAuthHeaders }
  );

  if (response.status !== 202 || response.data?.status !== "accepted") {
    throw new Error("Playwright gateway did not accept the run request.");
  }

  return response.data as {
    status: "accepted";
    stream: string;
    message_id: string;
    event_id: string;
  };
};

export const getPlaywrightRuns = async (filters: PlaywrightRunFilters = {}) => {
  const params = {
    ...(filters.label?.length ? { label: filters.label } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
  };
  const response = await apiClient.get(`${PLAYWRIGHT_RUNS_PATH}/runs`, { params });
  const data = response.data;
  return (Array.isArray(data) ? data : data?.runs ?? data?.items ?? []) as PlaywrightRun[];
};

export const getPlaywrightRun = async (id: string) => {
  const response = await apiClient.get(`${PLAYWRIGHT_RUNS_PATH}/runs/${id}`);
  return (response.data?.run ?? response.data) as PlaywrightRun;
};

export const getPlaywrightRunLogs = async (id: string) => {
  const response = await apiClient.get(`${PLAYWRIGHT_RUNS_PATH}/runs/${id}/logs`);
  return (response.data?.report ?? response.data) as PlaywrightRunReport;
};

export const getPlaywrightTestCatalog = async () => {
  const response = await apiClient.get(`${PLAYWRIGHT_RUNS_PATH}/catalog`);
  return response.data as PlaywrightTestCatalog;
};

export const getPlaywrightRunArtifacts = async (runId: string) => {
  const response = await apiClient.get(
    `${PLAYWRIGHT_RUNS_PATH}/runs/${runId}/artifacts`
  );
  return response.data as PlaywrightArtifactManifest;
};

export const getPlaywrightArtifactUrl = async (
  runId: string,
  artifactId: string,
  options: { download: boolean }
) => {
  const response = await apiClient.get(
    `${PLAYWRIGHT_RUNS_PATH}/runs/${runId}/artifacts/${artifactId}/url`,
    { params: { download: options.download } }
  );
  return response.data.url as string;
};

export const getPlaywrightTestBundleUrl = async (
  runId: string,
  testKey: string
) => {
  const response = await apiClient.get(
    `${PLAYWRIGHT_RUNS_PATH}/runs/${runId}/tests/${testKey}/download`
  );
  return response.data.url as string;
};

export const getPlaywrightRunBundleUrl = async (runId: string) => {
  const response = await apiClient.get(
    `${PLAYWRIGHT_RUNS_PATH}/runs/${runId}/download`
  );
  return response.data.url as string;
};
